import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { RdVerdict, SupplierListRow, VerificationBucket } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { AddBranchModal } from '../components/suppliers/AddBranchModal';
import { BulkReverifyBar } from '../components/suppliers/BulkReverifyBar';
import { ChangeHistoryDrawer } from '../components/suppliers/ChangeHistoryDrawer';
import { ContactsSubTable } from '../components/suppliers/ContactsSubTable';
import {
  DefaultsCascadeEditor,
  effectiveValue,
} from '../components/suppliers/DefaultsCascadeEditor';
import { EditBranchModal } from '../components/suppliers/EditBranchModal';
import { InlineEditCell } from '../components/suppliers/InlineEditCell';
import { Supplier360Panel } from '../components/suppliers/Supplier360Panel';
import { SupplierDetail } from '../components/suppliers/SupplierDetail';
import { SupplierFormModal } from '../components/suppliers/SupplierFormModal';
import { SupplierImportModal } from '../components/suppliers/SupplierImportModal';
import { SupplierVerdictChip } from '../components/suppliers/SupplierVerdictChip';
import { VendorBankSection } from '../components/suppliers/VendorBankSection';
import {
  bucketOf,
  VerificationDashboard,
  type SupplierVerifyRow,
} from '../components/suppliers/VerificationDashboard';
import { WhtDefaultFields } from '../components/suppliers/WhtDefaultFields';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useBulkReverify,
  useSupplier,
  useSupplier360,
  useSuppliers,
  useUpdateBranch,
} from '../lib/queries/suppliers';

// RECONCILED with the real Plan A codebase (real file wins):
//  * `SupplierWithBranches` is NOT a `@dcc-erp/shared` export (the plan named one
//    that does not exist) — it is the local entity+branches shape declared in
//    `lib/queries/suppliers` (branch rows live under `.vendors`). Import it from
//    there, not from the shared barrel.
//  * `SupplierListRow.rdVerdict` is a plain `string | null` snapshot; narrow it to
//    `RdVerdict | null` for the chip (the chip enumerates exactly that union),
//    mirroring `SupplierDetail.tsx`.
//  * `@dcc-erp/ui` has no `arrow-left` icon (per the plan's own note) — use the
//    existing `chevron` icon for the back affordance; no new icon is added.
//  * The query hooks are companyId-scoped and return `{ data, isPending, error }`
//    (not `isLoading`); `useUpdateBranch` mutates with `{ id, vendorId, data }`
//    (the supplier id is required on the URL). The behavioral test asserts that
//    exact payload — NOT the plan body's `{ vendorId, data }` 2-arg shape.
import type { SupplierWithBranches } from '../lib/queries/suppliers';

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

type SupTab = 'active' | 'all';

export const Route = createFileRoute('/suppliers')({
  component: SuppliersPage,
});

// `SupplierListRow` carries no `rdVerifiedAt` (the aggregated row schema omits it),
// so the dashboard treats freshness as unknown → list rows bucket on verdict alone.
// A richer caller (or a mock) may still supply `rdVerifiedAt`; read it defensively.
function toVerifyRow(r: SupplierListRow): SupplierVerifyRow {
  return {
    id: r.id,
    nameTh: r.nameTh,
    taxId: r.taxId,
    rdVerdict: r.rdVerdict,
    rdVerifiedAt: (r as { rdVerifiedAt?: string | null }).rdVerifiedAt ?? null,
  };
}

// Exported so the behavioral test can mount the page body under its own router
// (sibling pattern: tests import the raw component, not Route.options.component).
export function SuppliersPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<SupTab>('active');
  const [q, setQ] = useState('');
  const [bucket, setBucket] = useState<VerificationBucket | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [addBranchFor, setAddBranchFor] = useState<SupplierWithBranches | null>(null);
  const [editBranch, setEditBranch] = useState<{
    supplier: SupplierWithBranches;
    vendorId: string | null;
  } | null>(null);
  // Supplier-level WHT default draft (INTENT only; cert is issued at payment).
  const [whtDraft, setWhtDraft] = useState<{ incomeType: string | null; rate: number | null }>({
    incomeType: null,
    rate: null,
  });

  // `SupplierListOpts.active` is `boolean` (optional, NOT `| undefined`); with
  // `exactOptionalPropertyTypes` we must OMIT the key on the "all" tab rather than
  // pass `active: undefined`.
  const {
    data: rows = [],
    isPending,
    error,
  } = useSuppliers(companyId, tab === 'active' ? { active: true } : {});
  const { data: detail } = useSupplier(companyId, selectedId ?? undefined);
  const s360 = useSupplier360(companyId, selectedId ?? undefined);
  const reverify = useBulkReverify(companyId);
  const updateBranch = useUpdateBranch(companyId);

  const filtered = useMemo(() => {
    let out = rows;
    if (q) {
      const n = q.toLowerCase();
      out = out.filter(
        (r) =>
          r.nameTh.toLowerCase().includes(n) ||
          (r.taxId ?? '').includes(n) ||
          r.code.toLowerCase().includes(n),
      );
    }
    if (bucket) out = out.filter((r) => bucketOf(toVerifyRow(r)) === bucket);
    return out;
  }, [rows, q, bucket]);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id)),
    );
  }

  // ---- Detail view -------------------------------------------------------
  if (selectedId && detail) {
    return (
      <div className="page">
        <PageHead
          crumbs={['ลูกหนี้ / เจ้าหนี้', 'ผู้ขาย', detail.nameTh]}
          title="รายละเอียดผู้ขาย"
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={() => setHistoryOpen(true)}>
                ประวัติการแก้ไข
              </button>
              <button className="btn" onClick={() => setSelectedId(null)}>
                <Icon name="chevron" size={14} /> กลับรายการ
              </button>
            </div>
          }
        />
        <div className="tbl-shell" style={{ padding: 20, display: 'grid', gap: 20 }}>
          <SupplierDetail
            supplier={detail}
            onAddBranch={() => setAddBranchFor(detail)}
            onEditEntity={() => setEditBranch({ supplier: detail, vendorId: null })}
            onEditBranch={(vendorId) => setEditBranch({ supplier: detail, vendorId })}
          />

          {/* M7 Phase 2 — vendor destination-bank (masked) + VBC 4-eyes change. A
              bank change is a VBC request (D2), never a direct quick-edit PATCH. */}
          {detail.vendors.map((b) => (
            <VendorBankSection
              key={`bank-${b.id}`}
              companyId={companyId}
              supplierId={detail.id}
              vendorId={b.id}
              vendor={b}
            />
          ))}

          {/* Supplier-360 read aggregate (open AP + aging + last-paid + YTD + docs). */}
          <Supplier360Panel data={s360.data} isLoading={s360.isPending} />

          {/* Cascade + WHT defaults — supplier-level defaults flow to each branch. */}
          <DefaultsCascadeEditor
            supplierDefaults={
              <div style={{ display: 'grid', gap: 12 }}>
                <WhtDefaultFields
                  incomeType={whtDraft.incomeType ?? detail.defaultWhtIncomeType ?? null}
                  rate={whtDraft.rate ?? detail.defaultWhtRate ?? null}
                  onChange={setWhtDraft}
                />
              </div>
            }
            branchEffective={detail.vendors.map((b) => {
              const eff = effectiveValue(b.terms, detail.terms);
              return {
                label: `${b.branchCode} · เครดิตเทอม`,
                value: eff.value,
                inherited: eff.inherited,
              };
            })}
          />

          {/* Safe inline-edit branches grid (terms / defaultExpenseCode / email). */}
          <div>
            <div className="meta" style={{ marginBottom: 4 }}>
              แก้ไขเร็ว (สาขา)
            </div>
            <div className="tbl-wrap">
              <table
                className="tbl"
                data-testid="branch-quickedit"
                style={{ width: '100%', fontSize: 13 }}
              >
                <thead>
                  <tr>
                    <th>รหัสสาขา</th>
                    <th>เครดิตเทอม</th>
                    <th>บัญชีเริ่มต้น</th>
                    <th>อีเมล</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.vendors.map((b) => (
                    <tr key={b.id}>
                      <td className="code">{b.branchCode}</td>
                      <td>
                        <InlineEditCell
                          field="terms"
                          value={b.terms ?? null}
                          onCommit={(field, next) =>
                            void updateBranch.mutateAsync({
                              id: detail.id,
                              vendorId: b.id,
                              data: { [field]: next },
                            })
                          }
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          field="defaultExpenseCode"
                          value={b.defaultExpenseCode ?? null}
                          onCommit={(field, next) =>
                            void updateBranch.mutateAsync({
                              id: detail.id,
                              vendorId: b.id,
                              data: { [field]: next },
                            })
                          }
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          field="email"
                          value={b.email ?? null}
                          onCommit={(field, next) =>
                            void updateBranch.mutateAsync({
                              id: detail.id,
                              vendorId: b.id,
                              data: { [field]: next },
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Contacts (chasing original documents). */}
          <div>
            <div className="meta" style={{ marginBottom: 4 }}>
              ผู้ติดต่อ
            </div>
            <ContactsSubTable companyId={companyId} supplierId={detail.id} />
          </div>
        </div>

        {addBranchFor && companyId && (
          <AddBranchModal
            companyId={companyId}
            supplierId={addBranchFor.id}
            supplierName={addBranchFor.nameTh}
            onClose={() => setAddBranchFor(null)}
          />
        )}
        {editBranch && companyId && (
          <EditBranchModal
            companyId={companyId}
            supplier={editBranch.supplier}
            vendorId={editBranch.vendorId}
            onClose={() => setEditBranch(null)}
          />
        )}
        {historyOpen && (
          <ChangeHistoryDrawer
            companyId={companyId}
            supplierId={detail.id}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </div>
    );
  }

  // ---- Aggregated list ---------------------------------------------------
  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'ผู้ขาย']}
        title="ผู้ขาย (Suppliers)"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <HelpButton guide="suppliers" />
            <button className="btn" onClick={() => setImportOpen(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn primary"
              onClick={() => setCreateOpen(true)}
              disabled={!companyId}
            >
              <Icon name="plus" size={14} /> เพิ่มผู้ขาย
            </button>
          </div>
        }
      />

      <div className="page-tabs">
        {(
          [
            ['active', 'ใช้งาน'],
            ['all', 'ทั้งหมด'],
          ] as const
        ).map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {/* Verification dashboard — bucket suppliers by RD verdict + freshness. */}
      <div style={{ margin: '12px 0' }}>
        <VerificationDashboard
          rows={rows.map(toVerifyRow)}
          active={bucket}
          onPickBucket={setBucket}
        />
      </div>

      <BulkReverifyBar
        selectedCount={selected.size}
        isPending={reverify.isPending}
        onReverify={() =>
          void reverify.mutateAsync([...selected]).then(() => setSelected(new Set()))
        }
        onClear={() => setSelected(new Set())}
      />

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา ชื่อผู้ขาย / เลขภาษี / รหัส"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 28,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '8px center',
            }}
          />
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 520 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    aria-label="select-all"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>รหัส</th>
                <th>ชื่อผู้ขาย</th>
                <th>เลขภาษี</th>
                <th className="num">สาขา</th>
                <th>ผลตรวจ RD</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {isPending && (
                <tr>
                  <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                    กำลังโหลด…
                  </td>
                </tr>
              )}
              {error && (
                <tr>
                  <td colSpan={7} className="neg" style={{ textAlign: 'center', padding: 20 }}>
                    โหลดไม่สำเร็จ: {error.message}
                  </td>
                </tr>
              )}
              {!isPending && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                    ไม่พบผู้ขาย
                  </td>
                </tr>
              )}
              {filtered.map((r: SupplierListRow) => (
                <tr key={r.id} style={{ cursor: 'pointer' }}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`select-${r.id}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                    />
                  </td>
                  <td className="code" onClick={() => setSelectedId(r.id)}>
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{r.code}</a>
                  </td>
                  <td onClick={() => setSelectedId(r.id)}>{r.nameTh}</td>
                  <td className="muted" onClick={() => setSelectedId(r.id)}>
                    {r.taxId ?? '—'}
                  </td>
                  <td className="num tnum" onClick={() => setSelectedId(r.id)}>
                    {r.branchCount}
                  </td>
                  <td onClick={() => setSelectedId(r.id)}>
                    <SupplierVerdictChip verdict={(r.rdVerdict as RdVerdict | null) ?? null} />
                  </td>
                  <td onClick={() => setSelectedId(r.id)}>
                    {r.isActive ? (
                      <span className="badge positive">
                        <span className="bdot" />
                        ใช้งาน
                      </span>
                    ) : (
                      <span className="badge neutral">
                        <span className="bdot" />
                        ปิดใช้งาน
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && companyId && (
        <SupplierFormModal
          companyId={companyId}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => setSelectedId(id)}
        />
      )}
      {importOpen && (
        <SupplierImportModal
          companyId={companyId}
          existingTaxIds={new Set(rows.map((r) => r.taxId).filter((t): t is string => !!t))}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}
